import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import Swal from "sweetalert2";

import ActionsMenu from "./ActionsMenu";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const initialForm = {
  module_id: "",
  module_tab_id: "",
  button_code: "",
  button_name: "",
  route_path: "",
  icon: "",
  display_order: 0,
  is_active: 1,
};

const ModuleButtons = () => {
  const [rows, setRows] = useState([]);

  const [modules, setModules] = useState([]);
  const [moduleTabs, setModuleTabs] = useState([]);
  const [allModuleTabs, setAllModuleTabs] = useState([]);

  const [form, setForm] = useState(initialForm);

  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const collegeId = user.college_id;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // ---------------------------------------------------------
  // LOAD MODULES
  // ---------------------------------------------------------

  const loadModules = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/modules/all/${clientId}/${collegeId}`
      );

      setModules(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Failed to load modules", error);

      showSnackbar(error.response?.data?.message || "Failed to load modules", "error");
    }
  };

  // ---------------------------------------------------------
  // LOAD TABS
  // ---------------------------------------------------------

  const loadModuleTabs = async () => {
    try {
      const url = `${API_BASE_URL}/api/rbacall/module-tabs/all/${clientId}/${collegeId}`;

      const response = await axios.get(url);

      const tabs = response.data?.data || response.data || [];

      setAllModuleTabs(Array.isArray(tabs) ? tabs : []);
      setModuleTabs([]);
    } catch (error) {
      console.error("Failed to load module tabs", error);

      setAllModuleTabs([]);
      setModuleTabs([]);

      showSnackbar(error.response?.data?.message || "Failed to load module tabs", "error");
    }
  };

  // ---------------------------------------------------------
  // LOAD BUTTONS
  // ---------------------------------------------------------

  // ---------------------------------------------------------
  // LOAD BUTTONS
  // ---------------------------------------------------------

  const loadButtons = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/module-buttons/${clientId}/${collegeId}`,
        {
          params: {
            page: page + 1,
            pageSize,
            search,
          },
        }
      );

      const result = response.data;

      // API response:
      // {
      //   success: true,
      //   data: {
      //     items: [],
      //     total: 1,
      //     page: 1,
      //     pageSize: 10,
      //     totalPages: 1
      //   }
      // }

      const items = result?.data?.items || result?.items || result?.rows || [];

      const total =
        result?.data?.total ?? result?.total ?? result?.totalCount ?? result?.rowCount ?? 0;

      setRows(
        Array.isArray(items)
          ? items.map((item) => ({
              ...item,

              // DataGrid requires id
              id: item.module_button_id,
            }))
          : []
      );

      setRowCount(Number(total));
    } catch (error) {
      console.error("Failed to load module buttons", error);

      setRows([]);
      setRowCount(0);

      showSnackbar(error.response?.data?.message || "Failed to load module buttons", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId) {
      loadModules();
      loadModuleTabs();
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (clientId && collegeId) {
      loadButtons();
    }
  }, [clientId, collegeId, page, pageSize, search]);

  // ---------------------------------------------------------
  // MODULE CHANGE
  // ---------------------------------------------------------
  const filterTabsByModule = (moduleId) => {
    if (!moduleId) {
      setModuleTabs([]);
      return;
    }

    const filteredTabs = allModuleTabs.filter((tab) => String(tab.module_id) === String(moduleId));

    setModuleTabs(filteredTabs);
  };
  const handleModuleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      module_id: value,
      module_tab_id: "",
    }));

    filterTabsByModule(value);
  };

  // ---------------------------------------------------------
  // OPEN CREATE
  // ---------------------------------------------------------

  const handleAdd = () => {
    setEditId(null);

    setForm({
      ...initialForm,
    });

    setModuleTabs([]);

    setOpenDialog(true);
  };

  // ---------------------------------------------------------
  // OPEN EDIT
  // ---------------------------------------------------------

  const handleEdit = (row) => {
    setEditId(row.module_button_id);

    const moduleId = row.module_id ? String(row.module_id) : "";
    const moduleTabId = row.module_tab_id ? String(row.module_tab_id) : "";

    setForm({
      module_id: moduleId,
      module_tab_id: moduleTabId,
      button_code: row.button_code || "",
      button_name: row.button_name || "",
      route_path: row.route_path || "",
      icon: row.icon || "",
      display_order: row.display_order ?? 0,
      is_active: row.is_active ? 1 : 0,
    });

    // Filter tabs for selected module
    const filteredTabs = allModuleTabs.filter((tab) => String(tab.module_id) === moduleId);

    setModuleTabs(filteredTabs);

    setOpenDialog(true);
  };

  // ---------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------

  const handleSave = async () => {
    try {
      if (!form.module_id) {
        showSnackbar("Please select module", "error");
        return;
      }

      if (!form.module_tab_id) {
        showSnackbar("Please select module tab", "error");
        return;
      }

      if (!form.button_code.trim()) {
        showSnackbar("Button code is required", "error");
        return;
      }

      if (!form.button_name.trim()) {
        showSnackbar("Button name is required", "error");
        return;
      }

      const payload = {
        ...form,
        client_id: clientId,
        college_id: collegeId,
      };

      if (editId) {
        await axios.put(
          `${API_BASE_URL}/api/rbacall/module-buttons/${clientId}/${collegeId}/${editId}`,
          payload
        );

        showSnackbar("Module button updated successfully");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/rbacall/module-buttons/${clientId}/${collegeId}`,
          payload
        );

        showSnackbar("Module button created successfully");
      }

      setOpenDialog(false);
      loadButtons();
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to save module button", "error");
    }
  };

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Delete Module Button?",
      text: `Are you sure you want to delete "${row.button_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/module-buttons/${clientId}/${collegeId}/${row.module_button_id}`
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Module button deleted successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      loadButtons();
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete module button",
      });
    }
  };

  // ---------------------------------------------------------
  // COLUMNS
  // ---------------------------------------------------------

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
      renderCell: (params) => page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "module_name",
      headerName: "Module",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "tab_name",
      headerName: "Tab",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "button_code",
      headerName: "Button Code",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "button_name",
      headerName: "Button Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "route_path",
      headerName: "Route",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "display_order",
      headerName: "Order",
      width: 90,
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Typography color="success.main">Active</Typography>
        ) : (
          <Typography color="error.main">Inactive</Typography>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu
          onEdit={() => handleEdit(params.row)}
          onDelete={() => handleDelete(params.row)}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={2}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">MODULE BUTTONS</Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ color: "#fff" }}
            >
              Add Button
            </Button>
          </Stack>
        </Toolbar>

        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      {/* DIALOG */}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Module Button" : "Add Module Button"}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Module"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.module_id}
              onChange={(e) => handleModuleChange(e.target.value)}
              SelectProps={{ native: true }}
              required
            >
              <option value="">Select Module</option>

              {modules.map((item) => (
                <option key={item.module_id} value={item.module_id}>
                  {item.module_name}
                </option>
              ))}
            </TextField>

            <TextField
              select
              label="Module Tab"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.module_tab_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  module_tab_id: e.target.value,
                })
              }
              SelectProps={{ native: true }}
              required
            >
              <option value="">Select Tab</option>

              {moduleTabs.map((item) => (
                <option key={item.module_tab_id} value={item.module_tab_id}>
                  {item.tab_name}
                </option>
              ))}
            </TextField>

            <TextField
              label="Button Code"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.button_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  button_code: e.target.value.toUpperCase(),
                })
              }
              required
            />

            <TextField
              label="Button Name"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.button_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  button_name: e.target.value,
                })
              }
              required
            />

            <TextField
              label="Route Path"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.route_path}
              onChange={(e) =>
                setForm({
                  ...form,
                  route_path: e.target.value,
                })
              }
            />

            <TextField
              label="Icon"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.icon}
              onChange={(e) =>
                setForm({
                  ...form,
                  icon: e.target.value,
                })
              }
            />

            <TextField
              label="Display Order"
              type="number"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.display_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  display_order: Number(e.target.value),
                })
              }
            />

            <TextField
              select
              label="Status"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: Number(e.target.value),
                })
              }
              SelectProps={{ native: true }}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>

          <Button variant="contained" onClick={handleSave} sx={{ color: "#fff" }}>
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleButtons;
